/**
 * Behaviour Settings Route
 * Channel behaviour configuration with integrated playground
 */

import { useEffect, useCallback, useState } from 'react';
import { useAtomValue } from 'jotai';
import { toast } from '@/components/ui/sonner';
import { Chatbot, DEFAULT_SUGGESTED_QUERIES } from '@/modules/chatbot';
import { BehaviourCard } from '@/modules/behaviour';
import { useChannels } from '@/hooks/use-channels';
import { useChat, useChatMessages } from '@/hooks/use-chat';
import { useWebSocket } from '@/hooks/use-websocket';
import { currentUserAtom } from '@/hooks/use-auth';
import { usePermissions } from '@/hooks/use-permissions';
import { ReadOnlyProvider } from '@/lib/read-only-context';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { LandPlot, Minus } from 'lucide-react';
import type { MessageTypeValue } from '@/types/websocket.types';
import type { Channel } from '@/types';

const BehaviourCardSkeleton = () => (
  <div className='flex flex-col gap-3'>
    <Card className='p-4 flex flex-col gap-5'>
      {/* Channel icon + name */}
      <div className='flex items-center gap-2'>
        <Skeleton className='h-5 w-5 rounded' />
        <Skeleton className='h-4 w-24' />
      </div>

      {/* Tone of voice section */}
      <div className='flex flex-col gap-2'>
        <Skeleton className='h-4 w-24' />
        <div className='flex flex-wrap gap-2'>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className='h-8 w-20 rounded-md' />
          ))}
        </div>
      </div>

      {/* Answer Length section */}
      <div className='flex flex-col gap-2'>
        <Skeleton className='h-4 w-28' />
        <div className='flex flex-wrap gap-2'>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className='h-8 w-16 rounded-md' />
          ))}
        </div>
      </div>

      {/* Communication Style textarea */}
      <div className='flex flex-col gap-2'>
        <Skeleton className='h-4 w-36' />
        <Skeleton className='h-[115px] w-full rounded-md' />
      </div>
    </Card>
  </div>
);

export default function Behaviour() {
  const currentUser = useAtomValue(currentUserAtom);
  const { can } = usePermissions();
  const behaviourReadOnly = !can('edit', 'memory.brand');
  const { data: channels, isLoading: channelsLoading } = useChannels();
  const { messages } = useChatMessages();
  const { addUserMessage, addRunReply } = useChat();
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [showPlayground, setShowPlayground] = useState(false);

  const { sendMessage, setMessageHandler, setErrorHandler, isConnected, isWaiting } = useWebSocket();

  // Set up message handlers
  useEffect(() => {
    setMessageHandler((data) => {
      if (data.success && data.response && data.response.length > 0) {
        addRunReply(data.response[0], data.rules_used, data.request_id);
      } else {
        const errorMessage = 'Sorry, I encountered an error processing your request.';
        addRunReply(errorMessage, undefined, undefined, true);
        toast.error('Failed to get response');
      }
    });

    setErrorHandler((data) => {
      toast.error(data.message || 'An error occurred');
      addRunReply(`Error: ${data.message || 'An unexpected error occurred'}`, undefined, undefined, true);
    });
  }, [setMessageHandler, setErrorHandler, addRunReply]);

  // Set first channel as default when channels load
  useEffect(() => {
    if (channels && channels.length > 0 && !selectedChannel) {
      setSelectedChannel(channels[0]);
    }
  }, [channels, selectedChannel]);

  const handleSendMessage = useCallback(
    (content: string, channelId?: string) => {
      const effectiveChannelId = channelId || selectedChannel?.id;

      // Add user message immediately
      addUserMessage(content, effectiveChannelId);

      // Get account ID from current user
      const accountId = currentUser?.accountId != null ? String(currentUser.accountId) : undefined;
      if (!accountId) {
        toast.error('Please log in to send messages');
        return;
      }

      if (!isConnected) {
        toast.error('Not connected to server');
        return;
      }

      // Send message via WebSocket
      sendMessage({
        type: 'TEST_RULE' as MessageTypeValue,
        payload: {
          messages: [
            {
              role: 'user',
              content: [
                {
                  type: 'text',
                  text: content
                }
              ]
            }
          ],
          channel: effectiveChannelId
        },
        accountId,
        channel: effectiveChannelId
      });
    },
    [selectedChannel?.id, addUserMessage, currentUser?.accountId, isConnected, sendMessage]
  );

  const handleChannelChange = useCallback(
    (channelId: string) => {
      setSelectedChannel(channels?.find((channel) => channel.id === channelId) || null);
    },
    [channels]
  );

  return (
    <div className='flex gap-0 h-screen'>
      {/* First Column - Auto Width */}
      <div className='flex-1 flex flex-col gap-6 border-r p-4 overflow-y-auto'>

        {/* <h1 className='text-base font-bold'>Behaviour</h1>
        <div className='flex-1 space-y-6'>
          <div className='flex items-start justify-between border rounded-md p-3'>
            <div className='space-y-1'>
              <p className='text-sm font-medium'>Shadow Mode</p>
              <p className='text-xs text-muted-foreground max-w-md'>
                Toggle to enable shadow mode options. When disabled, shadow-only environments are hidden in training pages.
              </p>
            </div>
            <Switch checked={shadowModeEnabled} onCheckedChange={setShadowModeEnabled} />
          </div> */}

        <div className='flex items-start justify-between'>
          <h1 className='text-base font-semibold'>Behaviour</h1>
          {!showPlayground && (
            <Button variant='outline' size='sm' onClick={() => setShowPlayground(true)} className='gap-2 text-xs w-20'>
              <LandPlot className='h-4 w-4' />
              Test
            </Button>
          )}
        </div>
        <div className='flex-1'>
          {channelsLoading ? (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
              {[1, 2, 3, 4].map((i) => (
                <BehaviourCardSkeleton key={i} />
              ))}
            </div>
          ) : channels && channels.length > 0 ? (
            <ReadOnlyProvider value={behaviourReadOnly}>
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {channels.map((channel) => (
                  <BehaviourCard key={channel.id} channel={channel} />
                ))}
              </div>
            </ReadOnlyProvider>
          ) : (
            <div className='text-muted-foreground'>No channels found</div>
          )}
        </div>
      </div>

      {/* Second Column - Fixed 350px Width */}
      {showPlayground && (
        <div className='w-[350px] h-screen flex flex-col'>
          <div className='flex items-center justify-between p-4'>
            <h1 className='text-base font-semibold flex items-center gap-3'>
              <LandPlot className='h-4 w-4' />
              Test
            </h1>
            <Button variant='outline' size='icon' onClick={() => setShowPlayground(false)} className='h-8 w-8 border-none'>
              <Minus className='h-4 w-4' />
            </Button>
          </div>
          <div className='flex-1 flex items-center justify-center overflow-hidden pb-2'>
            {channelsLoading ? (
              <div className='text-muted-foreground'>Loading...</div>
            ) : (
              <Chatbot
                messages={messages}
                suggestions={DEFAULT_SUGGESTED_QUERIES}
                onSendMessage={handleSendMessage}
                onChannelChange={handleChannelChange}
                isLoading={isWaiting}
                availableChannels={channels || []}
                selectedChannelId={selectedChannel?.id || ''}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
